import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Download, 
  Code2, 
  FileJson, 
  XCircle,
  Copy,
  Hash,
  Loader2,
  Zap,
  RefreshCw
} from 'lucide-react';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Export Panel - Deterministic Compilation with Live Preview
 * 
 * The TypeScript preview auto-compiles when:
 * - The panel first renders (tab opened)
 * - Any option toggle changes
 * - Export format changes
 */
const ExportPanel = ({ machineId, machine }) => {
  const [compiledData, setCompiledData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exportTarget, setExportTarget] = useState('typescript');
  const [options, setOptions] = useState({
    includeComments: true,
    includeTypes: true,
    includeFactory: true,
    strictMode: true,
    minify: false,
    targetRuntime: 'esm'
  });

  // Track if we've done the initial compile
  const initialCompileDone = useRef(false);
  const debounceTimer = useRef(null);

  const compile = useCallback(async (silent = false) => {
    if (!machineId) return;
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/machines/${machineId}/compile`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ target: exportTarget, options })
        }
      );
      if (!response.ok) throw new Error('Compilation failed');
      const data = await response.json();
      setCompiledData(data);
      if (!silent && data.success === false) {
        toast.error('Export blocked - see errors');
      }
    } catch (error) {
      if (!silent) toast.error('Compilation failed');
    } finally {
      setLoading(false);
    }
  }, [machineId, exportTarget, options]);

  // Auto-compile on first mount (tab opened)
  useEffect(() => {
    if (machineId && !initialCompileDone.current) {
      initialCompileDone.current = true;
      compile(true);
    }
  }, [machineId, compile]);

  // Re-compile on option or format change (debounced 400ms)
  useEffect(() => {
    if (!initialCompileDone.current) return;
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      compile(true);
    }, 400);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [exportTarget, options, compile]);

  // Reset on machine change
  useEffect(() => {
    initialCompileDone.current = false;
    setCompiledData(null);
  }, [machineId]);

  const download = useCallback((content, filename) => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${filename}`);
  }, []);

  const copyToClipboard = useCallback((content) => {
    navigator.clipboard.writeText(content);
    toast.success('Copied to clipboard');
  }, []);

  const updateOption = (key, value) => {
    setOptions(prev => ({ ...prev, [key]: value }));
  };

  const getOutputContent = () => {
    if (!compiledData) return null;
    if (exportTarget === 'typescript') return compiledData.code;
    if (exportTarget === 'json-ir') return JSON.stringify(compiledData.ir, null, 2);
    return null;
  };

  const getFilename = () => {
    const name = machine?.name || 'machine';
    if (exportTarget === 'typescript') return `${name}.ts`;
    if (exportTarget === 'json-ir') return `${name}.ir.json`;
    return `${name}.txt`;
  };

  const outputContent = getOutputContent();

  return (
    <div className="h-full flex flex-col" data-testid="export-panel">
      {/* Header */}
      <div className="px-4 py-2 border-b border-white/10 bg-black/30 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold">Export</h3>
            <p className="text-[10px] text-gray-500">Deterministic compilation with provenance</p>
          </div>
          <div className="flex items-center space-x-2">
            {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-400" />}
            {compiledData?.provenance && (
              <Badge variant="outline" className="text-[10px] text-green-400 border-green-500/30">
                <Hash className="w-3 h-3 mr-1" />
                {compiledData.provenance.content_hash?.substring(0, 8)}
              </Badge>
            )}
          </div>
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-3">
          {/* Export Format Selection */}
          <div className="space-y-2">
            <Label className="text-xs text-gray-400">Export Format</Label>
            <RadioGroup 
              value={exportTarget} 
              onValueChange={setExportTarget}
              className="flex space-x-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="typescript" id="ts" />
                <Label htmlFor="ts" className="text-sm flex items-center cursor-pointer">
                  <Code2 className="w-3.5 h-3.5 mr-1.5" />
                  TypeScript
                  <Badge variant="outline" className="ml-1.5 text-[9px] px-1 h-4 text-green-400 border-green-500/30">
                    PRIMARY
                  </Badge>
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="json-ir" id="ir" />
                <Label htmlFor="ir" className="text-sm flex items-center cursor-pointer">
                  <FileJson className="w-3.5 h-3.5 mr-1.5" />
                  JSON IR
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Options Grid - Compact */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 p-3 bg-black/20 rounded border border-white/10">
            <div className="col-span-2 text-[10px] text-gray-500 uppercase tracking-wider mb-1">Options</div>
            
            <CompactSwitch
              id="comments"
              label="Comments"
              checked={options.includeComments}
              onCheckedChange={(v) => updateOption('includeComments', v)}
            />
            <CompactSwitch
              id="types"
              label="Type Defs"
              checked={options.includeTypes}
              onCheckedChange={(v) => updateOption('includeTypes', v)}
            />
            <CompactSwitch
              id="factory"
              label="Factory Fn"
              checked={options.includeFactory}
              onCheckedChange={(v) => updateOption('includeFactory', v)}
            />
            <CompactSwitch
              id="strict"
              label="Strict"
              checked={options.strictMode}
              onCheckedChange={(v) => updateOption('strictMode', v)}
            />
            <CompactSwitch
              id="minify"
              label="Minify"
              checked={options.minify}
              onCheckedChange={(v) => updateOption('minify', v)}
            />
            
            <div className="flex items-center justify-between">
              <Label className="text-xs">Runtime</Label>
              <Select 
                value={options.targetRuntime} 
                onValueChange={(v) => updateOption('targetRuntime', v)}
              >
                <SelectTrigger className="h-7 w-20 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="esm">ESM</SelectItem>
                  <SelectItem value="cjs">CJS</SelectItem>
                  <SelectItem value="umd">UMD</SelectItem>
                  <SelectItem value="iife">IIFE</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Live Preview */}
          {outputContent ? (
            <div className="border border-white/10 rounded overflow-hidden" data-testid="export-preview">
              {/* Preview Header */}
              <div className="flex items-center justify-between px-3 py-1.5 bg-black/40 border-b border-white/10">
                <div className="flex items-center space-x-2">
                  <div className={cn(
                    "w-1.5 h-1.5 rounded-full",
                    loading ? "bg-yellow-400 animate-pulse" : "bg-green-400"
                  )} />
                  <span className="text-[10px] text-gray-400 font-mono">{getFilename()}</span>
                  {compiledData?.provenance?.validation?.passed && (
                    <Badge variant="outline" className="text-[9px] h-3.5 px-1 text-green-400 border-green-500/20">valid</Badge>
                  )}
                </div>
                <div className="flex items-center space-x-0.5">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => compile(false)}
                    className="h-6 w-6 p-0"
                    title="Recompile"
                    data-testid="recompile-btn"
                  >
                    <RefreshCw className={cn("w-3 h-3", loading && "animate-spin")} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(outputContent)}
                    className="h-6 w-6 p-0"
                    title="Copy"
                    data-testid="copy-export-btn"
                  >
                    <Copy className="w-3 h-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => download(outputContent, getFilename())}
                    className="h-6 w-6 p-0"
                    title="Download"
                    data-testid="download-export-btn"
                  >
                    <Download className="w-3 h-3" />
                  </Button>
                </div>
              </div>

              {/* Code Output */}
              <pre className="p-3 text-[11px] font-mono text-gray-300 whitespace-pre overflow-x-auto max-h-[500px] overflow-y-auto bg-[#0d1117]" data-testid="export-code-output">
                {loading ? (
                  <span className="text-gray-500 italic">Recompiling...</span>
                ) : (
                  <code>{outputContent}</code>
                )}
              </pre>
            </div>
          ) : loading ? (
            <div className="border border-white/10 rounded p-8 text-center bg-[#0d1117]">
              <Loader2 className="w-5 h-5 mx-auto mb-2 animate-spin text-blue-400" />
              <p className="text-xs text-gray-500">Compiling preview...</p>
            </div>
          ) : !machineId ? (
            <div className="text-center py-8 text-sm text-gray-500">
              Select a machine to preview export
            </div>
          ) : null}

          {/* Blocks / Errors */}
          {compiledData?.blocks?.length > 0 && (
            <div className="p-2 bg-red-500/10 border border-red-500/30 rounded">
              <div className="flex items-center text-red-400 text-xs font-bold mb-1">
                <XCircle className="w-3.5 h-3.5 mr-1" />
                Export Blocked
              </div>
              {compiledData.blocks.map((block, idx) => (
                <div key={idx} className="text-[10px] text-gray-300 mt-1">
                  <span className="text-red-300 font-mono">{block.type}:</span> {block.message}
                </div>
              ))}
            </div>
          )}

          {/* Provenance */}
          {compiledData?.provenance && (
            <ProvenanceCompact provenance={compiledData.provenance} />
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

const CompactSwitch = ({ id, label, checked, onCheckedChange }) => (
  <div className="flex items-center justify-between">
    <Label htmlFor={id} className="text-xs">{label}</Label>
    <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} className="scale-75" />
  </div>
);

const ProvenanceCompact = ({ provenance }) => (
  <div className="p-2 bg-black/30 border border-white/10 rounded space-y-2">
    <div className="grid grid-cols-4 gap-2 text-[10px]">
      <div>
        <div className="text-gray-500">Machine</div>
        <div className="font-mono text-white truncate">{provenance.machine_name}</div>
      </div>
      <div>
        <div className="text-gray-500">Hash</div>
        <div className="font-mono text-white">{provenance.content_hash?.substring(0, 8)}</div>
      </div>
      <div>
        <div className="text-gray-500">Compiler</div>
        <div className="font-mono text-white">v{provenance.compiler_version}</div>
      </div>
      <div>
        <div className="text-gray-500">Validation</div>
        <div className={cn("font-medium", provenance.validation?.passed ? "text-green-400" : "text-red-400")}>
          {provenance.validation?.passed ? 'PASS' : 'FAIL'}
        </div>
      </div>
    </div>
    
    {provenance.capabilities_required?.length > 0 && (
      <div className="flex items-center gap-1 flex-wrap">
        <Zap className="w-3 h-3 text-gray-500" />
        {provenance.capabilities_required.map(cap => (
          <Badge key={cap} variant="outline" className="text-[9px] px-1 h-4 font-mono">
            {cap}
          </Badge>
        ))}
      </div>
    )}
  </div>
);

export default ExportPanel;
